import React, { useEffect, useMemo, useState } from 'react'
import axiosInstant from '../../api'
import {
  getProductCategories,
  rmnBrandsEndpoint,
  rmnCategoriesEndpoint,
  rmnProductsEndpoint,
} from '../../api/endpoints'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import { RmnCategoryNode, RmnNetworkBrand } from '../../types/rmn'
import {
  filterSelectClass,
  flattenTdc,
  TdcCategoryRow,
} from '../admin/productsV2/productListView/ProductFilters'
import {
  InlineProductSelector,
  Product,
} from '../videoProduction/components/InlineProductSelector'

// supplier_brand_id (not rmn_brand_id) — that param name is reserved for
// internal users' rmnControl context root and would collide.
export type StrategyFilterKey =
  | 'product_category_id'
  | 'rmn_category_id'
  | 'supplier_brand_id'
  | 'desireid'

interface FlatOption {
  id: number
  label: string
  depth: number
}

// Depth-first flatten of the crawled category TREE (the TDC list arrives
// flat and goes through flattenTdc instead).
const flattenRmnTree = (nodes: RmnCategoryNode[], depth = 0): FlatOption[] =>
  nodes.flatMap((n) => [
    { id: n.id, label: n.name, depth },
    ...flattenRmnTree(n.children || [], depth + 1),
  ])

// Strategies-list filters (TDC-1499): both category systems side by side —
// the TDC taxonomy (every strategy stores one) and the network's crawled
// retailer tree (matched via the proposal_meta stamp or the linked
// products' staging rows) — plus the network's supplier brands and a
// specific-product picker (imported products only: strategy links always
// resolve to desires). Values live in the URL; the parent wires them to
// useResourceList extraParams.
export function StrategyFilters({
  productCategoryId,
  rmnCategoryId,
  brandId,
  desireid,
  onChange,
}: {
  productCategoryId: string
  rmnCategoryId: string
  brandId: string
  desireid: string
  onChange: (key: StrategyFilterKey, value: string | null) => void
}) {
  const { brandId: accountBrandId } = useNetworkScope()
  const [tdcRows, setTdcRows] = useState<TdcCategoryRow[]>([])
  const [rmnTree, setRmnTree] = useState<RmnCategoryNode[]>([])
  const [brands, setBrands] = useState<RmnNetworkBrand[]>([])
  // Title of the picked product for the chip — falls back to "Product #id"
  // on deep links (there is no by-id product endpoint to resolve it).
  const [productTitle, setProductTitle] = useState<string | null>(null)

  useEffect(() => {
    let stale = false
    axiosInstant
      .get(getProductCategories())
      .then((res) => {
        if (!stale) setTdcRows((res.data || []) as TdcCategoryRow[])
      })
      .catch(() => {
        if (!stale) setTdcRows([])
      })
    axiosInstant
      .get(rmnCategoriesEndpoint)
      .then((res) => {
        if (!stale) setRmnTree((res.data || []) as RmnCategoryNode[])
      })
      .catch(() => {
        if (!stale) setRmnTree([])
      })
    axiosInstant
      .get(rmnBrandsEndpoint)
      .then((res) => {
        if (stale) return
        // The endpoint returns the account brand itself too — the filter is
        // for its supplier brands only.
        const rows = (res.data || []) as RmnNetworkBrand[]
        setBrands(rows.filter((b) => b.id !== accountBrandId))
      })
      .catch(() => {
        if (!stale) setBrands([])
      })
    return () => {
      stale = true
    }
  }, [accountBrandId])

  const tdcOptions = useMemo(() => flattenTdc(tdcRows), [tdcRows])
  const rmnOptions = useMemo(() => flattenRmnTree(rmnTree), [rmnTree])

  const handleProductPick = (product: Product | null) => {
    if (!product || product.desireid == null) return
    setProductTitle(product.title || null)
    onChange('desireid', String(product.desireid))
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {tdcOptions.length > 0 && (
        <select
          aria-label="Filter by TDC category"
          value={productCategoryId}
          onChange={(e) => onChange('product_category_id', e.target.value || null)}
          className={filterSelectClass}
        >
          <option value="">All categories</option>
          {tdcOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {' '.repeat(o.depth * 3)}
              {o.label}
            </option>
          ))}
        </select>
      )}
      {rmnOptions.length > 0 && (
        <select
          aria-label="Filter by retailer category"
          value={rmnCategoryId}
          onChange={(e) => onChange('rmn_category_id', e.target.value || null)}
          className={filterSelectClass}
        >
          <option value="">All retailer categories</option>
          {rmnOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {' '.repeat(o.depth * 3)}
              {o.label}
            </option>
          ))}
        </select>
      )}
      {brands.length > 0 && (
        <select
          aria-label="Filter by brand"
          value={brandId}
          onChange={(e) => onChange('supplier_brand_id', e.target.value || null)}
          className={filterSelectClass}
        >
          <option value="">All brands</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      )}
      {desireid ? (
        <span className="flex items-center gap-1 text-[12px] font-semibold text-white bg-accent rounded-full pl-3 pr-1.5 py-1">
          {productTitle || `Product #${desireid}`}
          <button
            type="button"
            aria-label="Clear product filter"
            title="Clear this product filter"
            onClick={() => {
              setProductTitle(null)
              onChange('desireid', null)
            }}
            className="w-4 h-4 flex items-center justify-center rounded-full bg-transparent text-white border-0 cursor-pointer hover:bg-[#8A691F]"
          >
            ×
          </button>
        </span>
      ) : (
        <div style={{ width: 210 }}>
          <InlineProductSelector
            setFieldValue={() => {}}
            selectedProduct={null}
            setSelectedProduct={handleProductPick}
            selectedBrandId={undefined}
            productsEndpoint={rmnProductsEndpoint}
            allowAdd={false}
            requireBrand={false}
          />
        </div>
      )}
    </div>
  )
}
